import { isEmpty, noop, pickBy, union } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { SectionScoreRecommendations } from 'src/components/Listings/InventoryEventPage/InventoryEventDetail/components/Map/SectionScoreRecommendations';
import { useVenueMapHandlers } from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ScrollableToolbar } from 'src/core/ui/ScrollableToolbar';
import { useComparableListingsVenueMapColor } from 'src/hooks/useComparableListingsVenueMapColor';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CrossIcon } from 'src/svgs/Viagogo';
import { getMatchingSectionRow } from 'src/utils/seatScoreUtils';
import { mapToSelectedSectionSettings } from 'src/utils/types/selectedSectionSettingsWithOptionals';
import { onSectionSelctionWithMyRowDefaults } from 'src/utils/venueUtils';
import {
  Feature,
  Listing,
  SectionInfo,
  SelectedSectionSettings,
} from 'src/WebApiController';

import { useMobileListingDrawerContext } from '../MobileListingDrawerContext';
import * as styles from './MobileDrawerVenueMap.css';

type MobileDrawerVenueMapProps = {
  listing: Listing;
};

export const MobileDrawerVenueMap: React.FC<MobileDrawerVenueMapProps> = ({
  listing,
}) => {
  const hasDefaultToMyRowOnSectionSelectionFeature = useUserHasFeature(
    Feature.DefaultToMyRowOnSectionSelection
  );

  const hasSelectedSectionScoreRecommendationsFeature = useUserHasFeature(
    Feature.SelectedSectionScoreRecommendations
  );

  const {
    eventListings,
    listingPriceCalculation,
    pricingSettings,
    sectionRowRecommendations,
    onSavePricingSettings,
  } = useMobileListingDrawerContext();
  const { venueMapInfo } = useEventMapContext();

  const compListingSelectedSectionSettings =
    pricingSettings?.compListingSelectedSectionSettings ?? null;

  const onSelectedSectionsChange = useCallback(
    (selectedSections: SectionInfo[]) => {
      let compListingSelectedSectionSettingsNew: SelectedSectionSettings | null =
        null;
      if (listing && hasDefaultToMyRowOnSectionSelectionFeature) {
        compListingSelectedSectionSettingsNew =
          onSectionSelctionWithMyRowDefaults(
            compListingSelectedSectionSettings,
            selectedSections,
            venueMapInfo?.sections ?? [],
            listing
          );
      } else {
        const sectionIdFilter = selectedSections.map((s) => s.id);
        const selectedRowIds = selectedSections
          .flatMap(({ rows }) => rows)
          .map(({ id }) => id);
        const rowIdFilter = [];
        if (isEmpty(compListingSelectedSectionSettings?.rowIdFilter)) {
          rowIdFilter.push(...selectedRowIds);
        } else {
          rowIdFilter.push(
            ...(compListingSelectedSectionSettings?.rowIdFilter?.filter(
              (rowId) => selectedRowIds.includes(rowId)
            ) ?? [])
          );
        }
        const sectionRowIdFilter = pickBy(
          Object.assign(
            {},
            compListingSelectedSectionSettings?.sectionRowIdFilter ?? {}
          ),
          (_, key) => sectionIdFilter.includes(parseInt(key))
        );
        compListingSelectedSectionSettingsNew = {
          ...compListingSelectedSectionSettings,
          sectionIdFilter,
          rowIdFilter,
          sectionRowIdFilter,
        } as SelectedSectionSettings;
      }

      const sanitizedSelectedSectionSettings =
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
        null;

      onSavePricingSettings(sanitizedSelectedSectionSettings);
    },
    [
      listing,
      hasDefaultToMyRowOnSectionSelectionFeature,
      onSavePricingSettings,
      compListingSelectedSectionSettings,
      venueMapInfo?.sections,
    ]
  );

  const filterSections = useMemo(() => {
    const selectedSectionId =
      compListingSelectedSectionSettings?.sectionIdFilter ?? [];
    return (
      venueMapInfo?.sections?.filter(({ id }) =>
        selectedSectionId.includes(id)
      ) ?? []
    );
  }, [
    compListingSelectedSectionSettings?.sectionIdFilter,
    venueMapInfo?.sections,
  ]);

  // marked section ids in the venue map
  const compListingSectionIds = useMemo(() => {
    const sectionNames = (listingPriceCalculation?.compListings ?? []).map(
      (l) => l.section
    );
    const sectionIds = (venueMapInfo?.sections ?? [])
      .filter((section) => sectionNames.includes(section.name))
      .map(({ id }) => id);

    return union(
      sectionIds,
      pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter ?? []
    );
  }, [
    listingPriceCalculation?.compListings,
    pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter,
    venueMapInfo?.sections,
  ]);

  const {
    onSectionClicked,
    onToggleMirrors,
    onSectionHovered,
    onSectionTapAndHold,
  } = useVenueMapHandlers(
    filterSections,
    listingPriceCalculation ?? undefined,
    venueMapInfo,
    onSelectedSectionsChange
  );

  const selectedSectionIds = useMemo(() => {
    if (!listing?.seating) {
      return [];
    }

    const { section } = getMatchingSectionRow(
      listing.seating,
      venueMapInfo?.sections
    );
    return section ? [section.id] : [];
  }, [listing?.seating, venueMapInfo?.sections]);

  const getColor = useComparableListingsVenueMapColor(
    eventListings ?? [],
    listingPriceCalculation?.compListings,
    listingPriceCalculation?.compListings
  );

  const recommendedSectionIds = useMemo(() => {
    if (!sectionRowRecommendations) {
      return undefined;
    }
    return (
      sectionRowRecommendations.recommendedSelectedSectionSettings
        .sectionIdFilter ?? undefined
    );
  }, [sectionRowRecommendations]);

  const onSaveRecommendation = useCallback(() => {
    if (sectionRowRecommendations?.recommendedSelectedSectionSettings) {
      onSavePricingSettings(
        sectionRowRecommendations.recommendedSelectedSectionSettings
      );
    }
  }, [
    onSavePricingSettings,
    sectionRowRecommendations?.recommendedSelectedSectionSettings,
  ]);

  const onDeleteSection = useCallback(
    (sectionIdToDelete: number) => {
      const selectedSections = (venueMapInfo?.sections ?? []).filter((s) =>
        pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter?.includes(
          s.id
        )
      );
      const updated = selectedSections.filter(
        (s) => s.id !== sectionIdToDelete
      );
      onSelectedSectionsChange(updated);
    },
    [
      onSelectedSectionsChange,
      pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter,
      venueMapInfo?.sections,
    ]
  );

  const sectionIdLookup = useMemo(
    () =>
      (venueMapInfo?.sections ?? []).reduce(
        (acc, s) => {
          acc[s.id] = s;
          return acc;
        },
        {} as Record<number, SectionInfo>
      ),
    [venueMapInfo?.sections]
  );

  const sectionsContent = useMemo(() => {
    if (
      hasSelectedSectionScoreRecommendationsFeature &&
      Boolean(recommendedSectionIds?.length)
    ) {
      return (
        <SectionScoreRecommendations
          useSave
          recommendingSectionIds={recommendedSectionIds}
          onDeclineRecommendation={noop}
          onSaveRecommendation={onSaveRecommendation}
        />
      );
    }

    const selectedSections =
      pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter ??
      [];
    if (!isEmpty(selectedSections)) {
      return (
        <ScrollableToolbar
          scrollAreaClassName={styles.scrollalbeArea}
          scrollButtonClassName={styles.scrollButton}
        >
          <Stack gap="s" style={{ flex: 1 }}>
            {selectedSections.map((sectionId) => {
              const section = sectionIdLookup[sectionId];
              if (!section) {
                return null;
              }
              return (
                <SelectedSectionItem
                  key={section.id}
                  section={section}
                  onDelete={() => onDeleteSection(section.id)}
                />
              );
            })}
          </Stack>
        </ScrollableToolbar>
      );
    }

    return undefined;
  }, [
    hasSelectedSectionScoreRecommendationsFeature,
    onDeleteSection,
    onSaveRecommendation,
    pricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter,
    recommendedSectionIds,
    sectionIdLookup,
  ]);

  return (
    <Stack direction="column" width="full" className={styles.container}>
      {sectionsContent && (
        <Stack alignItems="start" style={{ maxWidth: '100%' }}>
          <div className={styles.recommendation}>{sectionsContent}</div>
        </Stack>
      )}
      <div style={{ width: '100%', aspectRatio: '7/6' }}>
        <EventVenueMap
          isCondensedView
          selectedSectionIds={selectedSectionIds}
          markedSectionIds={compListingSectionIds}
          animatedSectionIds={recommendedSectionIds}
          onSectionClicked={onSectionClicked}
          onSectionTapAndHold={onSectionTapAndHold}
          onSectionHovered={onSectionHovered}
          onToggleMirrors={onToggleMirrors}
          setSelectedSections={onSelectedSectionsChange}
          getColor={getColor}
          allowDrawLine={false}
          condensedControl
          mapActionContainerClassname={styles.actionContainer}
        />
      </div>
    </Stack>
  );
};

export const SelectedSectionItem = ({
  section,
  onDelete,
}: {
  section: SectionInfo;
  onDelete: () => void;
}) => {
  return (
    <Stack
      alignItems="center"
      direction="row"
      gap="s"
      className={styles.sectionItem}
    >
      <span>{section.name}</span>
      <CrossIcon withHoverEffect onClick={onDelete} size={vars.iconSize.s} />
    </Stack>
  );
};
